import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RiCalendarLine,
  RiUserLine,
  RiGraduationCapLine,
  RiFileTextLine,
  RiArrowRightSLine,
  RiPresentationLine,
  RiRefreshLine,
  RiSearchLine,
  RiCloseLine,
  RiListCheck,
  RiTeamLine,
  RiAwardLine,
} from 'react-icons/ri';
import { API_BASE } from '../config';
import { Link } from 'react-router-dom';
import { useSummit } from '../pages/summitdetails'; // Import the useSummit hook

const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth;-webkit-font-smoothing:antialiased}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  ::-webkit-scrollbar{width:8px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:40px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-bottom:20px}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(2rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:14px}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(.88rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}
  .t-stats{display:flex;gap:1px;background:${T.border};border-radius:14px;border:1px solid ${T.border};overflow:hidden;align-self:flex-start;flex-shrink:0}
  .t-st{background:${T.white};padding:20px 28px;display:flex;flex-direction:column;gap:4px;min-width:100px}
  .t-st:nth-child(2){background:${T.offWhite}}
  .t-st-n{font-family:'Playfair Display',serif;font-size:1.8rem;font-weight:900;color:${T.accentGold};line-height:1;letter-spacing:-.03em}
  .t-st-l{font-size:11px;color:${T.textMuted};font-weight:500;text-transform:uppercase;letter-spacing:.08em}

  .t-sessions-wrapper{padding:40px clamp(20px,4vw,64px) 56px;background:${T.offWhite}}
  .t-sessions-container{max-width:1320px;margin:0 auto}
  .t-session-card{background:${T.white};border-radius:20px;border:1px solid ${T.border};overflow:hidden;box-shadow:${T.shadow};transition:all .3s ease;margin-bottom:16px}
  .t-session-card:hover{border-color:${T.primaryLight};box-shadow:${T.shadowMd}}
  .t-session-header{display:flex;align-items:center;justify-content:space-between;padding:20px 24px;cursor:pointer;transition:background .2s}
  .t-session-header:hover{background:${T.softTeal}}
  .t-session-left{display:flex;align-items:center;gap:16px;flex:1}
  .t-session-order{width:40px;height:40px;border-radius:12px;background:${T.primary}15;display:flex;align-items:center;justify-content:center;font-weight:800;color:${T.primary}}
  .t-session-info{flex:1}
  .t-session-title{font-family:'Playfair Display',serif;font-size:18px;font-weight:800;color:${T.navy};margin-bottom:6px}
  .t-session-meta{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
  .t-session-badge{display:inline-flex;align-items:center;gap:6px;padding:4px 12px;border-radius:20px;font-size:11px;font-weight:600;background:${T.softTeal};color:${T.primary}}
  .t-session-expand{width:32px;height:32px;border-radius:8px;display:flex;align-items:center;justify-content:center;color:${T.textMuted};transition:all .2s}
  .t-session-header:hover .t-session-expand{color:${T.primary};background:${T.softTeal}}
  .t-session-expand.open svg{transform:rotate(90deg)}

  .t-session-body{overflow:hidden}
  .t-session-inner{padding:4px 24px 24px 80px;border-top:1px solid ${T.borderLight}}
  .t-presentation-item{display:flex;gap:16px;padding:16px 0;border-bottom:1px solid ${T.borderLight}}
  .t-presentation-item:last-child{border-bottom:none}
  .t-presentation-icon{width:40px;height:40px;border-radius:12px;background:${T.softTeal};display:flex;align-items:center;justify-content:center;flex-shrink:0;color:${T.primary}}
  .t-presentation-content{flex:1}
  .t-presentation-title{font-size:15px;font-weight:700;color:${T.text};margin-bottom:6px}
  .t-presentation-speaker{display:flex;align-items:center;gap:8px;font-size:13px;color:${T.primary};margin-bottom:4px}
  .t-presentation-affiliation{font-size:12px;color:${T.textMuted}}
  .t-presentation-desc{font-size:13px;color:${T.textMid};margin-top:8px;padding-left:12px;border-left:2px solid ${T.primary}30}

  .t-sb{position:relative;max-width:400px;margin:0 auto 32px}
  .t-si{width:100%;height:44px;padding:0 40px;border-radius:12px;border:1px solid ${T.border};background:${T.white};color:${T.text};font-family:'Inter',sans-serif;font-size:14px;outline:none;transition:all .2s}
  .t-si:focus{border-color:${T.primary};box-shadow:0 0 0 3px rgba(42,157,143,.12)}
  .t-si-icon{position:absolute;left:14px;top:50%;transform:translateY(-50%);color:${T.textMuted}}
  .t-si-clr{position:absolute;right:14px;top:50%;transform:translateY(-50%);cursor:pointer;color:${T.textMuted};background:none;border:none}

  .t-stats-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:20px;margin-bottom:40px}
  .t-stat-card{background:${T.white};border-radius:16px;border:1px solid ${T.border};padding:20px;text-align:center;transition:all .3s}
  .t-stat-card:hover{transform:translateY(-4px);box-shadow:${T.shadowMd}}
  .t-stat-icon{width:48px;height:48px;border-radius:14px;background:${T.softTeal};display:flex;align-items:center;justify-content:center;margin:0 auto 12px;color:${T.primary}}
  .t-stat-value{font-family:'Playfair Display',serif;font-size:32px;font-weight:900;color:${T.navy};line-height:1}
  .t-stat-label{font-size:12px;color:${T.textMuted};margin-top:6px;font-weight:500}

  .t-dates-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:16px;margin-bottom:40px}
  .t-date-card{background:${T.white};border-radius:16px;border:1px solid ${T.border};padding:16px 20px;display:flex;align-items:center;gap:14px;transition:all .2s}
  .t-date-card:hover{transform:translateX(4px);border-color:${T.primaryLight}}
  .t-date-icon{width:44px;height:44px;border-radius:12px;background:${T.softTeal};display:flex;align-items:center;justify-content:center;color:${T.primary}}
  .t-date-info{flex:1}
  .t-date-title{font-weight:700;color:${T.text};margin-bottom:4px}
  .t-date-value{font-size:13px;color:${T.textMuted}}

  .t-cta-w{padding:0 clamp(20px,4vw,64px) 48px;background:${T.offWhite}}
  .t-cta{max-width:1320px;margin:0 auto;background:${T.gradNavy};border:1px solid rgba(59,188,174,0.28);border-radius:20px;padding:clamp(32px,4vw,52px) clamp(32px,4vw,60px);display:flex;align-items:center;justify-content:space-between;gap:32px;flex-wrap:wrap;position:relative;overflow:hidden}
  .t-cta::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 50% 100%,rgba(212,160,23,0.12) 0%,transparent 60%);pointer-events:none}
  .t-cta-tag{display:inline-block;padding:4px 12px;border-radius:20px;background:rgba(212,160,23,0.15);border:1px solid rgba(212,160,23,0.25);color:${T.accentGoldLight};font-size:11px;letter-spacing:.08em;text-transform:uppercase;margin-bottom:12px;font-weight:700}
  .t-cta-h{font-family:'Playfair Display',serif;font-size:clamp(1.4rem,2.5vw,2rem);font-weight:900;color:${T.white};letter-spacing:-.025em;line-height:1.15}
  .t-cta-p{font-size:14px;color:${T.textMuted};margin-top:8px;line-height:1.6}
  .t-cta-btn{display:inline-flex;align-items:center;gap:8px;padding:13px 28px;background:${T.gradGold};color:${T.navyDeep};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s}
  .t-cta-btn:hover{background:${T.gradGoldReverse};transform:translateY(-2px);box-shadow:${T.shadowGold}}

  .t-sw{max-width:1320px;margin:0 auto;display:flex;flex-direction:column;align-items:center;text-align:center;padding:72px 24px;gap:12px}
  .t-s-icon{width:52px;height:52px;border-radius:13px;background:${T.softTeal};border:1px solid ${T.border};display:flex;align-items:center;justify-content:center;font-size:22px;color:${T.primary};margin-bottom:4px}
  .t-s-title{font-family:'Playfair Display',serif;font-size:20px;font-weight:800;color:${T.navy}}
  .t-s-sub{font-size:14px;color:${T.textMuted};max-width:300px;line-height:1.6}
  .t-s-btn{margin-top:4px;padding:9px 20px;border-radius:8px;border:1px solid ${T.border};background:${T.white};color:${T.text};font-family:'Inter',sans-serif;font-size:13px;font-weight:500;cursor:pointer;transition:all .15s}
  .t-s-btn:hover{border-color:${T.primary};color:${T.primary}}

  @keyframes spin{to{transform:rotate(360deg)}}
  @media(max-width:768px){
    .t-stats-grid{grid-template-columns:repeat(2,1fr)}
    .t-session-inner{padding:4px 16px 16px 16px}
  }
`;

const Hero = ({ edition, totalSessions, totalPresentations }) => (
  <section className="t-hero">
    <div className="t-hero-i">
      <div>
        <div className="t-hero-lbl">
          <RiPresentationLine size={13} />
          Meetings21 · Scientific Program
        </div>
        <h1 className="t-hero-h">
          Preliminary <em>Program</em>
        </h1>
        <p className="t-hero-p">
          Explore the comprehensive scientific program featuring plenary sessions,
          keynote presentations, and interactive workshops from world-renowned experts.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
          <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
          <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
          <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
        </div>
      </div>

      {edition && (
        <div className="t-stats">
          <div className="t-st">
            <span className="t-st-n">{edition}</span>
            <span className="t-st-l">Edition</span>
          </div>
          <div className="t-st">
            <span className="t-st-n">{totalSessions}</span>
            <span className="t-st-l">Sessions</span>
          </div>
          <div className="t-st">
            <span className="t-st-n">{totalPresentations}</span>
            <span className="t-st-l">Presentations</span>
          </div>
        </div>
      )}
    </div>
  </section>
);

const ProgrammeOverview = () => {
  // Get eventId from SummitContext instead of useParams
  const { eventId, summit } = useSummit();
  console.log("ProgrammeOverview - eventId from context:", eventId);
  console.log("ProgrammeOverview - summit:", summit);
  
  const [programmeData, setProgrammeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSessions, setExpandedSessions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const fetchProgrammeData = async () => {
    console.log("fetchProgrammeData called with eventId:", eventId);
    
    if (!eventId) {
      console.log("No eventId provided from context");
      setError("No event ID provided");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/programme/event/${eventId}`;
      console.log("Fetching from URL:", url);
      
      const response = await axios.get(url);
      
      console.log("Full API Response:", response);
      console.log("Response data:", response.data);

      if (response.data?.success && response.data?.data) {
        const programme = response.data.data;
        console.log("Programme object:", programme);
        
        setProgrammeData({
          _id: programme._id,
          edition: programme.edition || summit?.title?.split(' ').pop() || "2024",
          isActive: programme.isActive !== undefined ? programme.isActive : true,
          keyDates: programme.keyDates || [],
          sessions: programme.sessions || []
        });
        console.log("ProgrammeData set successfully");
      } else {
        console.log("No programme found - response:", response.data);
        setError("No programme found for this event");
        setProgrammeData(null);
      }
    } catch (err) {
      console.error("Error in fetchProgrammeData:", err);
      
      if (err.response?.status === 404) {
        setError("Programme has not been created for this event yet");
        setProgrammeData(null);
      } else {
        setError(err.response?.data?.message || "Failed to fetch programme data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("useEffect triggered, eventId:", eventId);
    if (eventId) {
      fetchProgrammeData();
    }
  }, [eventId]);

  const toggleSession = (sessionIndex) => {
    setExpandedSessions(prev => ({ 
      ...prev, 
      [sessionIndex]: !prev[sessionIndex] 
    }));
  };

  const sortedSessions = useMemo(() => {
    if (!programmeData?.sessions || programmeData.sessions.length === 0) return [];
    
    return [...programmeData.sessions].sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    );
  }, [programmeData]);

  const filteredSessions = useMemo(() => {
    if (!sortedSessions.length) return [];

    if (!searchTerm.trim()) {
      return sortedSessions;
    }

    const term = searchTerm.toLowerCase();

    return sortedSessions.filter((session) => {
      const sessionMatch = session.title?.toLowerCase().includes(term);

      const presentationMatch = session.presentations?.some((presentation) =>
        presentation.presentationTitle?.toLowerCase().includes(term) ||
        presentation.speakerName?.toLowerCase().includes(term) ||
        presentation.speakerAffiliation?.toLowerCase().includes(term)
      );

      return sessionMatch || presentationMatch;
    });
  }, [sortedSessions, searchTerm]);

  const totalSessionsCount = sortedSessions.length;
  const totalPresentationsCount = sortedSessions.reduce(
    (total, s) => total + (s.presentations?.length || 0), 0
  );
  const totalSpeakers = [...new Set(
    sortedSessions.flatMap(s => s.presentations?.map(p => p.speakerName).filter(Boolean) || [])
  )].length;

  const activeKeyDates = (programmeData?.keyDates || []).filter(d => d.isEnabled === true);

  console.log("Rendering with programmeData:", programmeData);
  console.log("Sorted sessions:", sortedSessions);
  console.log("Loading state:", loading);
  console.log("Error state:", error);

  if (loading) {
    return (
      <div style={{ fontFamily: "'Inter', sans-serif", background: T.offWhite, minHeight: '100vh' }}>
        <style>{css}</style>
        <Hero edition="" totalSessions={0} totalPresentations={0} />
        <div className="t-sessions-wrapper">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{
              width: 48, height: 48,
              border: `3px solid ${T.border}`,
              borderTopColor: T.primary,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: T.textMuted }}>Loading program schedule...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !programmeData) {
    return (
      <div style={{ fontFamily: "'Inter', sans-serif", background: T.offWhite, minHeight: '100vh' }}>
        <style>{css}</style>
        <Hero edition="" totalSessions={0} totalPresentations={0} />
        <div className="t-sessions-wrapper">
          <div className="t-sw">
            <div className="t-s-icon"><RiFileTextLine size={24} /></div>
            <p className="t-s-title">Programme Coming Soon</p>
            <p className="t-s-sub">
              {error || "The scientific programme for this event is being prepared. Please check back later."}
            </p>
            <button className="t-s-btn" onClick={fetchProgrammeData}>
              <RiRefreshLine size={14} style={{ marginRight: 6 }} />
              Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (programmeData.isActive === false) {
    return (
      <div style={{ fontFamily: "'Inter', sans-serif", background: T.offWhite, minHeight: '100vh' }}>
        <style>{css}</style>
        <Hero edition="" totalSessions={0} totalPresentations={0} />
        <div className="t-sessions-wrapper">
          <div className="t-sw">
            <div className="t-s-icon"><RiFileTextLine size={24} /></div>
            <p className="t-s-title">Programme Inactive</p>
            <p className="t-s-sub">This programme is currently not active for public viewing.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: T.offWhite, minHeight: '100vh' }}>
      <style>{css}</style>

      <Hero
        edition={programmeData.edition || "2024"}
        totalSessions={totalSessionsCount}
        totalPresentations={totalPresentationsCount}
      />

      <div className="t-sessions-wrapper">
        <div className="t-sessions-container">
          {activeKeyDates.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="t-dates-grid"
            >
              {activeKeyDates.map((dateItem, idx) => (
                <div key={idx} className="t-date-card">
                  <div className="t-date-icon"><RiCalendarLine size={20} /></div>
                  <div className="t-date-info">
                    <div className="t-date-title">{dateItem.title || "Important Date"}</div>
                    <div className="t-date-value">
                      {dateItem.date ? format(new Date(dateItem.date), 'MMMM dd, yyyy') : 'Date not set'}
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {totalSessionsCount > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="t-stats-grid"
            >
              {[
                { icon: <RiListCheck size={22} />, value: totalSessionsCount, label: "Scientific Sessions" },
                { icon: <RiPresentationLine size={22} />, value: totalPresentationsCount, label: "Presentations" },
                { icon: <RiTeamLine size={22} />, value: totalSpeakers, label: "Expert Speakers" },
                { icon: <RiAwardLine size={22} />, value: "30+", label: "Countries" },
              ].map((stat, idx) => (
                <div key={idx} className="t-stat-card">
                  <div className="t-stat-icon">{stat.icon}</div>
                  <div className="t-stat-value">{stat.value}</div>
                  <div className="t-stat-label">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="t-sb"
          >
            <span className="t-si-icon"><RiSearchLine size={16} /></span>
            <input
              type="text"
              className="t-si"
              placeholder="Search sessions, presentations, or speakers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="t-si-clr" onClick={() => setSearchTerm('')}>
                <RiCloseLine size={16} />
              </button>
            )}
          </motion.div>

          {searchTerm && filteredSessions.length > 0 && (
            <p style={{ textAlign: 'center', fontSize: 13, color: T.textMuted, marginBottom: 24 }}>
              Found {filteredSessions.length} session{filteredSessions.length !== 1 ? 's' : ''} matching "{searchTerm}"
            </p>
          )}

          <AnimatePresence>
            {filteredSessions.length > 0 ? (
              filteredSessions.map((session, sessionIdx) => {
                const originalIndex = sortedSessions.findIndex(s => s.order === session.order);
                const isExpanded = expandedSessions[originalIndex] !== false;
                
                const sortedPresentations = session.presentations && session.presentations.length > 0
                  ? [...session.presentations].sort((a, b) => (a.order || 0) - (b.order || 0))
                  : [];

                return (
                  <motion.div
                    key={sessionIdx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: sessionIdx * 0.05 }}
                    className="t-session-card"
                  >
                    <div 
                      className="t-session-header" 
                      onClick={() => toggleSession(originalIndex)}
                    >
                      <div className="t-session-left">
                        <div className="t-session-order">
                          {session.order || sessionIdx + 1}
                        </div>
                        <div className="t-session-info">
                          <h3 className="t-session-title">
                            {session.title || 'Untitled Session'}
                          </h3>
                          <div className="t-session-meta">
                            <span className="t-session-badge">
                              <RiPresentationLine size={12} />
                              {sortedPresentations.length} Presentation{sortedPresentations.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className={`t-session-expand ${isExpanded ? 'open' : ''}`}>
                        <RiArrowRightSLine size={20} />
                      </div>
                    </div>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          className="t-session-body"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="t-session-inner">
                            {sortedPresentations.length > 0 ? (
                              sortedPresentations.map((presentation, presIdx) => (
                                <div key={presIdx} className="t-presentation-item">
                                  <div className="t-presentation-icon">
                                    <RiUserLine size={20} />
                                  </div>
                                  <div className="t-presentation-content">
                                    <h4 className="t-presentation-title">
                                      {presentation.presentationTitle || 'Untitled Presentation'}
                                    </h4>
                                    <div className="t-presentation-speaker">
                                      <RiGraduationCapLine size={14} />
                                      <span>{presentation.speakerName || 'TBD'}</span>
                                    </div>
                                    {presentation.speakerAffiliation && (
                                      <div className="t-presentation-affiliation">
                                        {presentation.speakerAffiliation}
                                      </div>
                                    )}
                                    {presentation.description && (
                                      <div className="t-presentation-desc">
                                        {presentation.description}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <p style={{ color: T.textMuted, padding: '12px 0', fontSize: 14 }}>
                                No presentations added to this session yet.
                              </p>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="t-sw"
                style={{ background: T.white, borderRadius: 20, padding: 60 }}
              >
                <div className="t-s-icon"><RiSearchLine size={28} /></div>
                <p className="t-s-title">No sessions found</p>
                <p className="t-s-sub">
                  {searchTerm 
                    ? 'Try adjusting your search or browse all sessions.' 
                    : 'No sessions have been added to this program yet.'}
                </p>
                {searchTerm && (
                  <button className="t-s-btn" onClick={() => setSearchTerm('')}>
                    Clear search
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="t-cta-w">
        <motion.div
          className="t-cta"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          viewport={{ once: true }}
        >
          <div>
            <div className="t-cta-tag">Register Now</div>
            <h2 className="t-cta-h">Join the Premier Academic Event</h2>
            <p className="t-cta-p">
              Secure your spot at the leading conference for academic and research professionals.
            </p>
          </div>
          <Link to={`/register?eventId=${eventId}`} className="t-cta-btn">
            Register Now
            <RiArrowRightSLine size={15} />
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default ProgrammeOverview;